import { Typography } from "@mui/material";
import { OutlinedTextFieldProps } from "@mui/material/TextField";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as Edit03Icon } from "components/Icons/edit-03.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, getIn } from "formik";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { Input } from "./Input";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    content: {
      border: `1px solid ${theme.primary500}`,
      borderRadius: "4px",
      background: theme.white00,
      display: "flex",
      alignItems: "stretch",
      width: "100%",
    },
    input: {
      width: "100%",
      border: 0,
      background: "transparent !important",
      paddingLeft: theme.spacing(5),

      "& textarea:focus": {
        outline: "1px solid transparent",
      },
    },
    wrapper: {
      display: "flex",
      alignItems: "center",
    },
    icon: {
      width: 20,
      height: 20,
      color: theme.gray500,
    },
    editIcon: {
      color: ({ iconDescription }: { iconDescription?: string }) =>
        iconDescription ? theme.primary500 : theme.gray500,
      marginLeft: theme.spacing(2),
      cursor: "pointer",
      width: 20,
      height: 20,
    },
    text: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.primary800,
    },
    actionIconsWrapper: {
      cursor: "pointer",
      borderLeft: `1px solid ${theme.primary500}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      minWidth: "32px",
    },
    contentToLong: {
      borderColor: theme.red500,
    },
    error: {
      color: theme.red500,
    },
    label: {
      maxWidth: "95%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: 14,
    },
    iconDescriptionWrapper: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      cursor: "pointer",
    },
    description: {
      fontSize: 14,
      color: theme.primary500,
    },
  }),
);

const defaultOptions = {
  maxLength: 255,
  disablePreviewTooltip: false,
  maxPreview: 60,
};

type Options = {
  maxLength?: number;
  maxPreview?: number;
  disablePreviewTooltip?: boolean;
};

type TextInputProps = Omit<OutlinedTextFieldProps, "variant"> & {
  "data-test-id"?: string;
  name: string;
  placeholder?: string;
  options?: Options;
  onEditModeOpen?: () => void;
  onEditModeClose?: (value?: string) => void;
  className?: string;
  autoFocus?: boolean;
  type?: "number" | "text";
  hideLabel?: boolean;
  iconDescription?: string;
};
export function InlineInput({
  name,
  placeholder,
  options,
  onEditModeOpen,
  onEditModeClose,
  className,
  autoFocus,
  "data-test-id": dataTestId = "input",
  type,
  hideLabel = false,
  iconDescription,
}: TextInputProps) {
  const classes = useStyles({ iconDescription });
  const { message } = useMessage();
  const [isEditing, setIsEditing] = useState(false);
  const { maxLength, maxPreview, disablePreviewTooltip } = {
    ...defaultOptions,
    ...options,
  };

  return (
    <Field name={name}>
      {({ field, form, meta }: { field: any; form: any; meta: any }) => {
        const isError =
          (meta.touched && meta.error) || getIn(form.status, field.name);

        const { setFieldValue, setFieldError } = form;
        const labelToRender =
          field.value && field.value.trim().length > 0
            ? field.value
            : placeholder;

        const isLabelPreviewToLong =
          maxPreview && labelToRender?.length >= maxPreview;

        return isEditing ? (
          <Input
            type={type}
            onEditModeClose={onEditModeClose}
            setFieldValue={(val: string) => setFieldValue(name, val)}
            setFieldError={() => setFieldError(name, undefined)}
            initialValue={field.value}
            setIsEditing={setIsEditing}
            maxLength={maxLength}
            isError={isError}
            autoFocus={autoFocus}
            dataTestId={dataTestId}
          />
        ) : (
          <div className={classes.wrapper}>
            {!hideLabel && (
              <Typography
                className={`${classes.label} ${isError ? classes.error : ""} ${className || ""}`}
                data-test-id={`${dataTestId}-label`}
              >
                <Tooltip
                  text={
                    isLabelPreviewToLong && !disablePreviewTooltip
                      ? labelToRender
                      : ""
                  }
                >
                  {isLabelPreviewToLong
                    ? `${labelToRender.substring(0, maxPreview)}...`
                    : labelToRender}
                </Tooltip>
              </Typography>
            )}
            <div
              className={classes.iconDescriptionWrapper}
              onClick={() => {
                setIsEditing(true);
                onEditModeOpen?.();
              }}
            >
              <Edit03Icon
                data-test-id={`${dataTestId}-edit`}
                className={classes.editIcon}
              />
              {iconDescription ? (
                <Typography className={classes.description}>
                  {message({ id: iconDescription })}
                </Typography>
              ) : null}
            </div>
          </div>
        );
      }}
    </Field>
  );
}
